import { mapState, mapGetters } from 'vuex';
import ApiService from '../../../services/apiService';
import NiotaClassicApiService from '../../../services/niotaClassicApiService';
import { EventBus } from '../../../services/eventbus.js';
import { isCopyToClipboardSupported, copyToClipboard } from '../../../utils/vuex';
import unsavedMixin from '../../../components/core/unsavedMixin';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
export default {
  name: 'ViewNiotaClassicDeviceTypesDeviceTypesDetails',
  mixins: [unsavedMixin],
  props: {
    title: {
      type: String,
      default: ''
    }
  },
  data: function () {
    return {
      deviceTypeId: null,
      deviceTypeData: null,
      canBeEdited: false,
      editedItem: null,
      formDialog: false,
      tabItem: 'details',
      healthStateConfig: {},
      healthStateConfigRegistryId: null,
      originalStateConfig: {},
      healthStateFormValid: false,
      linkedDeviceTypes: null,
      linkedVirtualDevices: null,
      tenantId: null,
      dialogSyncHealthStateOpen: false,
      linkedVirtualDevicesIds: []
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'permissions']),
    ...mapGetters({
      isFeatureFlagEnabled: 'app/isFeatureFlagEnabled'
    }),
    isDeviceDriverFallbakActive() {
      return this.isFeatureFlagEnabled('env_dispatcher_devicetype_fallback');
    },
    detailsLoaded() {
      return !this.deviceTypeData;
    },
    isIotHubManager() {
      return this.permissions['IotHub.manage'];
    },
    showCopyButton() {
      return isCopyToClipboardSupported && this.permissions['super.system.manage'];
    },
    healthCareButtons() {
      const applyButton = {
        icon: 'fa-sync',
        title: this.$i18n_t('Common.syncToVirtualDevices'),
        type: 'button',
        action: 'applied',
        valid: !this.formIsDirty,
        tooltip: this.formIsDirty ? this.$i18n_t('DigitalTwin.VirtualDevice.bulkUpdate.unsavedChanges') : ''
      };
      const arrayButtons = [{
        icon: 'fas fa-save',
        title: this.$i18n_t('Common.save'),
        type: 'button',
        action: 'submitted',
        valid: this.isIotHubManager ? this.healthStateFormValid : false
      }];
      arrayButtons.unshift(applyButton);
      return arrayButtons;
    },
    cardButtons() {
      return [...(this.canBeEdited ? [{
        icon: 'fal fa-edit',
        title: this.$i18n_t('Common.edit'),
        type: 'button',
        action: 'edit',
        valid: this.isIotHubManager
      }] : []), ...(this.showCopyButton ? [{
        title: this.$i18n_t('Common.copy'),
        icon: 'fal fa-clipboard',
        type: 'button',
        action: 'copy',
        valid: this.isIotHubManager
      }] : []), {
        icon: 'fal fa-times',
        type: 'button',
        color: 'danger',
        action: 'cancel'
      }];
    },
    virtualDeviceSearchTypes() {
      return [{
        text: this.$i18n_t('DigitalTwin.VirtualDevice.props.title'),
        param: 'title'
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.props.keyValue'),
        param: 'keyValue'
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.props')['bridge.type'],
        param: 'bridge.type',
        items: []
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.props.deviceId'),
        param: 'deviceId'
      }, {
        text: this.$i18n_t('Common.id'),
        param: 'id'
      }];
    }
  },
  watch: {
    $route() {
      // the component is not reloaded/re-mounted if the id in the route changes
      // so perform the setup again for a different route; i.e. swap out the displayed details on-the-fly
      this.loadDeviceTypeDetails();
    },
    healthStateConfig: {
      handler() {
        this.formIsDirty = !isEqual(this.originalStateConfig, this.healthStateConfig);
      },
      deep: true
    }
  },
  mounted() {
    this.loadDeviceTypeDetails();
    ApiService.getBridgeTypes().then(bridgeTypes => {
      (this.virtualDeviceSearchTypes.find(item => item.param === 'bridge.type') || {}).items = bridgeTypes.filter(bridge => {
        // this bridge-type must have a create-functionality
        // otherwise it should have a read to get 1 single existing device as import
        if (get(bridge, 'manifest.functionScope.extension.devices.create') || get(bridge, 'manifest.functionScope.extension.devices.read')) {
          return true;
        }
        return false;
      }).map(bridge => bridge.type.replace(/^bridge-/gi, ''));
    }).catch(error => {
      console.error(error);
    });
  },
  methods: {
    beforeRouteLeave(to, from, next) {
      // If the form is dirty and the user did not confirm leave,
      // prevent losing unsaved changes by canceling navigation
      if (this.formIsDirty) {
        this.unsavePromptNextPersist = next;
        this.unsavedPrompt = true;
      } else {
        if (this.$refs.parserWrapper) {
          this.$refs.parserWrapper.beforeRouteLeave(to, from, next);
        } else {
          next();
        }
      }
    },
    beforeWindowUnload(e) {
      if (this.formIsDirty) {
        // Cancel the event
        e.preventDefault();
        // Chrome requires returnValue to be set
        e.returnValue = '';
        // eslint-disable-next-line no-empty-function
        this.unsavePromptNextPersist = () => {};
        this.unsavedPrompt = true;
      } else {
        if (this.$refs.parserWrapper) {
          this.$refs.parserWrapper.beforeWindowUnload(e);
        }
      }
    },
    breadCrumbsPath() {
      if (get(this, 'deviceTypeData.account.name')) {
        return [this.permissions['all.manage'] ? {
          text: this.deviceTypeData.account.name,
          to: `/account-mgmt/${this.deviceTypeData.account.id}`
        } : this.deviceTypeData.account.name, this.deviceTypeData.title];
      }
      return [];
    },
    async loadDeviceTypeDetails() {
      this.deviceTypeId = this.$route.params.deviceTypeId;
      Promise.all([ApiService.virtualDeviceGetDeviceTypeHealthConfig(this.deviceTypeId), NiotaClassicApiService.deviceTypeGetDetails(this.deviceTypeId)]).then(res => {
        this.healthStateConfig = res[0].value || {};
        this.originalStateConfig = cloneDeep(this.healthStateConfig);
        this.formIsDirty = false;
        this.healthStateConfigRegistryId = res[0].id;
        const details = res[1].data;
        this.deviceTypeData = details.devicetype;
        this.canBeEdited = details.permission;
        if (this.$route.query.edit !== undefined) {
          this.handleEditDeviceType();
          // remove query param
          this.$router.replace(this.$route.path);
        }
        EventBus.$emit('update-breadcrumbs');
      }).catch(error => {
        this.healthStateConfig = {};
        this.originalStateConfig = cloneDeep(this.healthStateConfig);
        this.healthStateConfigRegistryId = null;
        this.deviceTypeData = null;
        this.$snack(get(error, 'response.data.err') || this.$i18n_t('NiotaClassic.DeviceTypes.snacks.fetchDetailsFailed'), 'error');
      });
      const linkedDeviceTypes = await NiotaClassicApiService.getDeviceTypeDevices(this.deviceTypeId, '', 1, 1);
      const linkedVirtualDevices = await ApiService.virtualDeviceFetch(this, null, [], null, null, null, 0, 1, [{
        variable: 'deviceType',
        search: this.deviceTypeId
      }], true);
      const devices = linkedVirtualDevices.virtualDevices || [];
      this.linkedDeviceTypes = linkedDeviceTypes.pagination.itemCount;
      this.linkedVirtualDevices = linkedVirtualDevices.totalCount;
      const scopes = devices.reduce((acc, element) => {
        if (!acc.includes(element.tenantId)) {
          acc.push(element.tenantId);
        }
        return acc;
      }, []);
      this.tenantId = scopes[0];
      this.linkedVirtualDevicesIds = devices.map(vd => vd.id);
    },
    closeSyncHealthStateDialog() {
      this.dialogSyncHealthStateOpen = false;
    },
    showDialogSyncHealthState() {
      this.dialogSyncHealthStateOpen = true;
    },
    openForm() {
      this.formDialog = true;
    },
    closeForm() {
      this.formDialog = false;
    },
    updateItem(item) {
      if (this.editedItem !== null) {
        this.deviceTypeData = cloneDeep(item);
      }
    },
    persistHealthStateConfig() {
      const value = this.healthStateConfig;
      const id = this.healthStateConfigRegistryId;
      ApiService.virtualDeviceUpdateDeviceTypeHealthConfig(this, this.deviceTypeId, value, id).then(res => {
        this.healthStateConfig = res.value;
        this.healthStateConfigRegistryId = res.id;
        this.originalStateConfig = cloneDeep(this.healthStateConfig);
        this.formIsDirty = false;
        this.$snack(this.$i18n_t('NiotaClassic.DeviceTypes.form.deviceHealth.saveSuccess'), 'success');
      }).catch(() => {
        this.$snack(this.$i18n_t('NiotaClassic.DeviceTypes.form.deviceHealth.saveError'), 'error');
      });
    },
    handleCopyDeviceType() {
      NiotaClassicApiService.deviceTypesGetCopy(this.deviceTypeId).then(res => {
        copyToClipboard(res.data.info);
        this.$snack(this.$i18n_t('NiotaClassic.DeviceTypes.snacks.copyClipboardSuccess'), 'success');
      }).catch(() => {
        this.$snack(this.$i18n_t('NiotaClassic.DeviceTypes.snacks.copyClipboardFailed'), 'error');
      });
    },
    handleEditDeviceType() {
      this.editedItem = cloneDeep(this.deviceTypeData);
      this.openForm();
    },
    getDeviceTypeImageUrl(image) {
      if (/^http(?:s):\/\//i.test(image)) {
        return image;
      }
      return `${process.env.VUE_APP_N1_API_PROTOCOL}://${process.env.VUE_APP_N1_API_HOST}${image}`;
    }
  }
};